import Layers from "slotworks/graphics/layers/layers";
import Translations from "slotworks/i18n/translations";
import RevolverInit from "slotworks/integration/revolver/states/revolver-init";
import autoplayModel from "slotworks/model/autoplay-model";
import SettingsModel, { Jurisdiction } from "slotworks/model/settings/settings-model";
import Prompt from "slotworks/components/prompt/prompt-content";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import Point from "slotworks/utils/geom/point";
import deviceInfo from "slotworks/utils/device-info";
import Contract from "slotworks/utils/contract";
import Components from "slotworks/components/components";
import { parallel, sequence } from "slotworks/utils/race-utils";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FlexiToggle, { ToggleEvent } from "slotworks/graphics/elements/flexi-toggle";
import HarleyComponents from "components/components";
import HarleySpineSymbolBehaviour from "components/matrix/symbol/spine-symbol-behaviour";
import IdleSpineSymbolBehaviour from "components/matrix/symbol/idle-symbol-behaviour";
import HarleyCelebrateWinComponent from "components/celebrate-win/celebrate-win";
import soundManager from "slotworks/sound/sound-manager";
import HarleyBannerComponent from "components/harley/banners";
import HarleySpinReelTransition from "components/matrix/reel/spin-reel-transition";
import HarleyLandSpineSymbolBehaviour from "components/matrix/symbol/land-spine-symbol-behaviour";
import HarleyAnticipateSpineSymbolBehaviour from "components/matrix/symbol/anticipate-spine-symbol-behaviour";
import gameLoop from "slotworks/core/game-loop";
import wrapperMediator from "slotworks/integration/revolver/wrapper/wrapper-mediator";
import betModel from "slotworks/model/bets/bet-model";
import currency from "slotworks/i18n/currency";

declare const __VERSION__: string;
export default class HarleyInit extends RevolverInit {

    private currentSlide: number = 1;
    private wrapperPaused: boolean = false;

    public onExit(): void {
        if (!SettingsModel.isUKJurisdiction() && SettingsModel.buyoutEngine) {
            HarleyComponents.bonusBuyButton.start();
        }
        Layers.Footer.show();
        HarleyComponents.harley.hide(true);
        // HarleyComponents.background.start();
        super.onExit();
    }

    public complete() {        
        HarleyComponents.init();
        this.setupHideBetbar();

        Components.banner.stop();
        Components.banner = new HarleyBannerComponent();

        Components.celebrateWin = new HarleyCelebrateWinComponent();

        const config = Components.config.matrix;
        let tconfig = config.transition.options;
        let transition = new HarleySpinReelTransition(tconfig.startGap, tconfig.stopGap, tconfig.quickStopGap, tconfig.allowPartialSkips)
        Components.matrix.setTransition(transition);

        HarleyComponents.reelAnimations.init();

        super.complete();  

        Layers.MenusContent.onSceneEnter.add((scene: string) => {
            const rulesText = Layers.MenusContent.getFlexiText("rules_body");
            const rtp = SettingsModel.rtp;
            const rtp_buyout = SettingsModel.rtp_buyout;
            const multiplier = SettingsModel.maxExp + "";
            
            if (rulesText) {
                if (SettingsModel.isJurisdiction([Jurisdiction.UK, Jurisdiction.DE, Jurisdiction.NL]) || autoplayModel.autoplayDisabled) {
                    rulesText.setText(Translations.get("pays_game_rules_jurisdiction", { version: __VERSION__, rtp_total: rtp + "",  rtp_buyout, multiplier }));
                } else if(SettingsModel.socialSweepstakes && SettingsModel.rtpdisplay === false) {
                    rulesText.setText(Translations.get("pays_game_rules_nortp", {version: __VERSION__, rtp_total: rtp + "",  rtp_buyout, multiplier}));
                } else {
                    rulesText.setText(Translations.get("rm.bod", { version: __VERSION__, rtp_total: rtp + "", rtp_buyout, multiplier }));
                }
            }

            let betText = Layers.MenusContent.getFlexiText("bet_text");
            if (betText) {
                let betOptions = betModel.generateBetOptions();
                let min = betOptions.coinSizes[0] * betOptions.stakePerLineValues[0] * betOptions.lines;
                let max = betOptions.coinSizes.slice(-1)[0] * betOptions.stakePerLineValues.slice(-1)[0] * betOptions.lines;
                let text = `${Translations.get('min_bet')} <orange>${currency.format(min)}</orange> | ${Translations.get('max_bet')} <orange>${currency.format(max)}</orange>`;
                betText.setText(text);
            }

            if (!deviceInfo.isDesktop) {
                const el = document.querySelector('#bet_text') as HTMLElement;
                if (el) {
                    if(scene === "paytable") {
                        el.style.display = 'block';
                    } else {
                        el.style.display = 'none';
                    }
                }
            }
        });

        if (!deviceInfo.isDesktop) {
            Layers.Menus.onSceneEnter.add((scene: string) => {
                let betText = Layers.Menus.getFlexiText("bet_text");
                if (betText) {
                    let betOptions = betModel.generateBetOptions();
                    let min = betOptions.coinSizes[0] * betOptions.stakePerLineValues[0] * betOptions.lines;
                    let max = betOptions.coinSizes.slice(-1)[0] * betOptions.stakePerLineValues.slice(-1)[0] * betOptions.lines;
                    let text = `${Translations.get('min_bet')} <orange>${currency.format(min)}</orange> | ${Translations.get('max_bet')} <orange>${currency.format(max)}</orange>`;
                    betText.setText(text);
                }
            });
        }
        
        this.setSymbolBehaviours();

        if (!deviceInfo.isDesktop) {
            canvasManager.onOrientationChange.add((orientation: number) => {
                if(orientation == Orientation.PORTRAIT) {
                    if(!this.wrapperPaused && gameLoop.isPaused()) gameLoop.setPaused(false);
                } else if(!gameLoop.isPaused()){
                    setTimeout(() => {
                        if(canvasManager.orientation == Orientation.LANDSCAPE) {
                            gameLoop.setPaused(true);
                        }
                    }, 150);
                }
            });

            wrapperMediator.onWrapperPauseChange.add((paused: boolean) => {
                this.wrapperPaused = paused;
            });
        }
    }

    protected showGameIntro() {       
        if (SettingsModel.getShowIntro()) {

            const gameIntro = new Prompt(["gameintro"]);
            const toggleHandler = (value: any) => {
                SettingsModel.setShowIntro(!value);
            };

            gameIntro.addContinueButton();
            gameIntro.addToggle("show", toggleHandler);

            canvasManager.setPortraitScale(this.portraitScale, new Point(0.5, 0.5));

            Layers.Custom.get("IntroSlides").show();

            Layers.Prompts.onSceneEnter.addOnce(this.initIntroSlides);

            const onLayoutChange = () => setTimeout(this.initIntroSlides, 1);
            if(!deviceInfo.isDesktop) {
                deviceInfo.onLayoutChange.add(onLayoutChange);
            }
            HarleyComponents.harley.showIdleIntro();
            return sequence([
                () => Layers.Background.setScene("gameintro"),
                () => Layers.Custom.get("IntroSlides").setScene("screen1"),                
                () => parallel([() => Components.prompt.showPrompt(gameIntro),
                                () => Components.soundIntro.show(gameIntro)]),
                () => Contract.wrap(() => {
                    HarleyComponents.harley.hideSpine();
                    deviceInfo.onLayoutChange.remove(onLayoutChange);
                    soundManager.customEvent("base_music");
                }),
                () => Layers.Custom.get("IntroSlides").defaultScene(),
                () => Layers.Background.defaultScene()
            ]);
            
        } else {
            return Contract.empty();
        }
    }

    private initIntroSlides = (scene: string) => {
        const layer = Layers.Prompts;
        const toggles: FlexiToggle[] = [];

        const clear = () => {
            for(const toggle of toggles) toggle.setValue(false);
        }

        const setSlide = (slide: number) => {
            clear();
            toggles[slide - 1].setValue(true);
            Layers.Custom.get("IntroSlides").setScene("screen" + slide).execute()
            this.currentSlide = slide;
        }; 

        const next = layer.getButton("nextpage");
        const prev = layer.getButton("prev");

        if(!next) return;
        
        next.on(ButtonEvent.POINTER_DOWN, () => {
            this.currentSlide++;
            if(this.currentSlide > 4) this.currentSlide = 1;
            setSlide(this.currentSlide);
        });

        prev.on(ButtonEvent.POINTER_DOWN, () => {
            this.currentSlide--;
            if(this.currentSlide < 1) this.currentSlide = 4;
            setSlide(this.currentSlide);
        });
        
        const slidesCount = 4;

        for(let i = 1; i <= slidesCount; i++) {
            const toggle = layer.getFlexiToggle("page" + i);
            toggles[i - 1] = toggle;
            toggle.setEnabled(true);
            toggle.on(ToggleEvent.CHANGE, () => setSlide(i));
        }

        setSlide(this.currentSlide);

        this.setUpMaxExposure();

        Layers.Custom.get("IntroSlides").onSceneEnter.add(this.setUpMaxExposure);
    }

    private setupHideBetbar() {
        Layers.Prompts.onSceneExit.add((scene: string) => {
            if (scene === "freespinssummary" || 
                    scene === "freespinswin" || 
                    scene === "sessionlimit" || 
                    scene === "confirm" || 
                    scene === "retrigger") {
                Layers.BetBar.show();
                Layers.SmartButton.show();
            }
        });

        Layers.Prompts.onSceneEnter.add((scene: string) => {
            if (scene !== "default" && scene !== "addfreespins" && scene !== "info" && scene !== "error") {
                Layers.BetBar.hide();
                Layers.SmartButton.hide();
            }
        });
    }

    private setUpMaxExposure = () => {
        const exposure = SettingsModel.engineVersionRTP + "";

        const exposures = {
            "90": "max_exposure_90_rtp",
            "92": "max_exposure_92_rtp",
            "94": "max_exposure_94_rtp",
            "96": "max_exposure_96_rtp"
        };
        const exposuresWithBuy = {
            "90": "max_exposure_90_rtp_with_buy",
            "92": "max_exposure_92_rtp_with_buy",
            "94": "max_exposure_94_rtp_with_buy",
            "96": "max_exposure_96_rtp_with_buy",
        }
        const sprites = [
            "max_exposure_90_rtp",
            "max_exposure_92_rtp",
            "max_exposure_94_rtp",
            "max_exposure_96_rtp",
            "max_exposure_90_rtp_with_buy",
            "max_exposure_92_rtp_with_buy",
            "max_exposure_94_rtp_with_buy",
            "max_exposure_96_rtp_with_buy",
        ];
        for(let s of sprites) {
            const sprite = Layers.Custom.get("IntroSlides").getSprite(s);
            if(sprite) {
                sprite.visible = false;
            }
        }
        const sprite = Layers.Custom.get("IntroSlides").getSprite( 
            SettingsModel.buyoutEngine ? 
            exposuresWithBuy[exposure as keyof typeof exposuresWithBuy] : 
            exposures[exposure  as keyof typeof exposures]);
        if(sprite) {
            sprite.visible = true;
        }

        this.setLinesAndRtp();
    }
    
    private setLinesAndRtp = () => {
        const text = Layers.Prompts.getText("[intro_screen_text_2_pt2]");
        if (text) {
            text.text = Translations.get("intro_screen_text_2_pt2", {rtp_total: SettingsModel.rtp});
        }
    }

    private setSymbolBehaviours() {
        let symbolBehaviours = Components.config.matrix.symbolBehaviours;
        if (symbolBehaviours) {
            for (const symbolBehaviour of symbolBehaviours) {
                for (const symbol of Components.matrix.getAllSymbols()) {
                    const behaviour = symbolBehaviour.id;
                    const filteredSymbols = symbolBehaviour.symbols;

                    let behaviourInstance;
                    if (behaviour == "harleyspine") {
                        behaviourInstance = new HarleySpineSymbolBehaviour(symbol);
                    } else if(behaviour == "idle") {
                        behaviourInstance = new IdleSpineSymbolBehaviour(symbol);
                    } else if(behaviour == "harleyland") {
                        behaviourInstance = new HarleyLandSpineSymbolBehaviour(symbol);
                    } else if(behaviour == "harleyanticipate") {
                        behaviourInstance = new HarleyAnticipateSpineSymbolBehaviour(symbol);
                    }

                    if(behaviourInstance) {
                        behaviourInstance.validSymbols = filteredSymbols;
                        symbol.addBehaviour(behaviourInstance);
                        
                        symbol.refresh();
                    }
                }
            }
        }
    }
}